import React, { useEffect, useRef } from "react";

export default function Terms() {
  const tocRef = useRef(null);
  const [activeId, setActiveId] = React.useState(null);

  // Keep the left Table of Contents scrolled proportionally with the page
  useEffect(() => {
    const el = tocRef.current;
    if (!el) return;

    let ticking = false;
    const syncTocScroll = () => {
      if (!el) return;
      const docEl = document.documentElement;
      const pageMax = Math.max(1, docEl.scrollHeight - window.innerHeight);
      const navMax = Math.max(0, el.scrollHeight - el.clientHeight);
      if (navMax === 0) return; // nothing to scroll inside TOC
      const progress = Math.min(1, Math.max(0, (window.scrollY || docEl.scrollTop) / pageMax));
      el.scrollTop = navMax * progress;
    };

    const onScrollOrResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          syncTocScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // initial position
    syncTocScroll();

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  const bodyTextCls =
    "font-sans text-[17px] md:text-[18px] leading-8 md:leading-9 tracking-[0.005em] text-gray-100";
  const panelListCls = "list-disc pl-5 mt-2 space-y-2";
  const h2Cls = "text-xl font-semibold mb-2";
  const h3Cls = "text-lg font-semibold mt-4 mb-1";
  const tocLinkCls = (id) =>
    `block rounded px-2 py-1 transition-colors underline-offset-4 hover:underline ${
      activeId === id
        ? "text-white font-semibold border-l-4 border-emerald-400 bg-slate-800/70"
        : "text-slate-200 hover:text-white"
    }`;

  const sectionCls = (id) =>
    `mb-12 rounded-md transition-colors ${activeId === id ? "ring-2 ring-emerald-400 ring-offset-0 ring-offset-transparent bg-slate-900/40" : ""}`;

  // Smooth scroll helper for TOC + update URL hash
  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.focus({ preventScroll: true });
      if (typeof window !== "undefined" && window.history && window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
      } else if (typeof window !== "undefined") {
        window.location.hash = `#${id}`;
      }
    }
  };

  // Observe sections to highlight the active TOC link
  useEffect(() => {
    const ids = [
      "highlights","how","memberships","after","acceptable","privacy","refunds",
      "disclaimers","beta","dispute","eligibility","changes","ip","indemnification",
      "accessibility","boilerplate"
    ];
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (sections.length === 0) return;

    let currentId = null;
    const observer = new IntersectionObserver(
      (entries) => {
        let top = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!top || entry.intersectionRatio > top.intersectionRatio) {
              top = entry;
            }
          }
        });
        if (top) {
          const id = top.target.id;
          if (id !== currentId) {
            currentId = id;
            setActiveId(id);
          }
        }
      },
      { root: null, rootMargin: "-30% 0px -55% 0px", threshold: [0.25, 0.4, 0.6] }
    );

    sections.forEach((sec) => observer.observe(sec));
    return () => observer.disconnect();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // On load, if there is a hash, gently scroll to it (after paint)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, []);

  return (
    <main className="w-full bg-gray-950 text-gray-100 antialiased print:bg-white print:text-black">
      <div id="top" />
      {/* Header */}
      <header className="bg-gradient-to-b from-slate-900 to-gray-900 border-b border-slate-800 print:bg-white print:text-black print:border-0">
        <div className="max-w-6xl xl:max-w-7xl mx-auto px-6 md:px-8 py-10">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white print:text-black">
            Scucci Systems LLC — Clear, Fair Terms, Privacy & Refunds
          </h1>
          <p className="mt-1 text-sm text-gray-300 print:text-black/80">
            (“Scucci”, “we”, “us,” or “our” refers to Scucci Systems LLC.)
          </p>
          <p className="mt-2 text-sm text-gray-300 print:text-black/80">Effective Date: October 30, 2025</p>
          <p className="mt-1 text-sm text-gray-400 print:text-black/70">Last Updated: November 29, 2025</p>
          <p className="mt-5 text-gray-100 max-w-3xl print:text-black">
            Welcome to <strong>Scucci Systems</strong>. Our goal is simple: help you stop handwriting,
            get professional tags made, and save hours on setup. These terms explain how the service
            works, how we protect both of us, and what to expect if plans change. This is just the
            beginning — as Scucci develops and grows, our features will grow with us — and with you.
            If anything isn’t clear, you can reply to any email from us or write{" "}
            <a
              className="underline text-sky-300 hover:text-sky-200 print:text-blue-700"
              href="mailto:support@scuccisystems.com"
            >
              support@scuccisystems.com
            </a>{" "}
            — a real person will help.
          </p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl xl:max-w-7xl mx-auto px-6 md:px-8 py-10 lg:grid lg:grid-cols-12 lg:gap-10">
        {/* TOC */}
        <aside className="lg:col-span-3 mb-10 lg:mb-0 print:hidden">
          <nav
            ref={tocRef}
            role="navigation"
            aria-label="Table of contents"
            className="rounded-lg border border-slate-800 bg-slate-900 p-6 lg:sticky lg:top-6 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto pr-2 text-slate-200"
          >
            <div className="font-sans text-base leading-7">
              <p className="font-semibold text-slate-100 mb-3">Table of Contents</p>
              <ul className="space-y-2 pr-2">
                <li>
                  <a href="#highlights" onClick={(e) => handleTocClick(e, "highlights")} className={tocLinkCls("highlights")}>
                    Plain-English Highlights
                  </a>
                </li>
                <li>
                  <a href="#how" onClick={(e) => handleTocClick(e, "how")} className={tocLinkCls("how")}>
                    1) How Scucci Works
                  </a>
                </li>
                <li>
                  <a href="#memberships" onClick={(e) => handleTocClick(e, "memberships")} className={tocLinkCls("memberships")}>
                    2) Memberships, Trials, Top-Ups &amp; Credits
                  </a>
                </li>
                <li>
                  <a href="#after" onClick={(e) => handleTocClick(e, "after")} className={tocLinkCls("after")}>
                    3) If You Take a Break
                  </a>
                </li>
                <li>
                  <a href="#acceptable" onClick={(e) => handleTocClick(e, "acceptable")} className={tocLinkCls("acceptable")}>
                    4) Acceptable Use (Keep It Professional)
                  </a>
                </li>
                <li>
                  <a href="#privacy" onClick={(e) => handleTocClick(e, "privacy")} className={tocLinkCls("privacy")}>
                    5) Privacy (Clear &amp; Practical)
                  </a>
                </li>
                <li>
                  <a href="#refunds" onClick={(e) => handleTocClick(e, "refunds")} className={tocLinkCls("refunds")}>
                    6) Refunds &amp; Billing Issues
                  </a>
                </li>
                <li>
                  <a href="#disclaimers" onClick={(e) => handleTocClick(e, "disclaimers")} className={tocLinkCls("disclaimers")}>
                    7) Important Disclaimers &amp; Limits
                  </a>
                </li>
                <li>
                  <a href="#beta" onClick={(e) => handleTocClick(e, "beta")} className={tocLinkCls("beta")}>
                    8) Beta Features
                  </a>
                </li>
                <li>
                  <a href="#dispute" onClick={(e) => handleTocClick(e, "dispute")} className={tocLinkCls("dispute")}>
                    9) Dispute Resolution (Arbitration) — Easy Opt-Out
                  </a>
                </li>
                <li>
                  <a href="#eligibility" onClick={(e) => handleTocClick(e, "eligibility")} className={tocLinkCls("eligibility")}>
                    10) Age, Eligibility &amp; Compliance
                  </a>
                </li>
                <li>
                  <a href="#changes" onClick={(e) => handleTocClick(e, "changes")} className={tocLinkCls("changes")}>
                    11) Changes to Prices or Terms
                  </a>
                </li>
                <li>
                  <a href="#ip" onClick={(e) => handleTocClick(e, "ip")} className={tocLinkCls("ip")}>
                    12) Intellectual Property &amp; DMCA
                  </a>
                </li>
                <li>
                  <a href="#indemnification" onClick={(e) => handleTocClick(e, "indemnification")} className={tocLinkCls("indemnification")}>
                    13) Indemnification
                  </a>
                </li>
                <li>
                  <a href="#accessibility" onClick={(e) => handleTocClick(e, "accessibility")} className={tocLinkCls("accessibility")}>
                    14) Accessibility &amp; Contact
                  </a>
                </li>
                <li>
                  <a href="#boilerplate" onClick={(e) => handleTocClick(e, "boilerplate")} className={tocLinkCls("boilerplate")}>
                    15) Legal Boilerplate
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* MAIN CONTENT */}
        <article className="lg:col-span-9 max-w-3xl print:max-w-none">
          {/* Highlights */}
          <section id="highlights" className={sectionCls("highlights")}>
            <div className="mb-4 rounded-md border border-emerald-800 bg-emerald-900/60 p-4 text-sm text-emerald-50">
              <p className="font-semibold">Plain-English Highlights</p>
              <ul className={panelListCls}>
                <li>
                  <strong>What you get:</strong> Take photos, Scucci drafts item entries. You review,
                  set prices, and print professional tags or export a clean CSV when your plan is
                  active.
                </li>
                <li>
                  <strong>Credits:</strong> 1 image = 1 Upload Credit. Credits are not money, are not
                  transferable, and are generally non-refundable. If you cancel, unused credits
                  remain for 24 months (see details below).
                </li>
                <li>
                  <strong>Active plan required:</strong> Inventory Dashboard, CSV/PDF exports, and
                  price-tag printing require an active plan (or active Free Trial).
                </li>
                <li>
                  <strong>If your plan ends:</strong> Members-only features pause. We hold your
                  previously created data for 90 days so you can reactivate.
                </li>
                <li>
                  <strong>Credits after cancel:</strong> Unused Upload Credits remain on your account
                  for 24 months from your paid-through date. Reactivate any time in that window to use
                  them. After 24 months, they expire. We send 60/30/7-day reminders.
                </li>
                <li>
                  <strong>Refunds:</strong> Memberships and Top-Ups are non-refundable. Billing mistake
                  or true hardship? Contact us—we’ll review with care.
                </li>
                <li>
                  <strong>No surprise billing:</strong> Cancel any time in-app.
                </li>
                <li>
                  <strong>Privacy:</strong> We don’t sell your personal data.
                </li>
                <li>
                  <strong>Age:</strong> For adults 18+.
                </li>
              </ul>
            </div>
          </section>

          {/* 1) How Scucci Works */}
          <section id="how" className={sectionCls("how")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>1) How Scucci Works</h2>
              <p><strong>Upload &amp; Draft.</strong> Each photo you upload uses 1 Upload Credit at upload. Scucci drafts titles/descriptions to save typing. You set prices and approve edits.</p>
              <p><strong>Output.</strong> With an active plan, print professional price tags or export a clean CSV.</p>
              <p><strong>Authority.</strong> Our internal credit ledger is the authoritative record of balances and transactions.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">
                Back to top ↑
              </a>
            </div>
          </section>

          {/* 2) Memberships, Trials, Top-Ups & Credits */}
          <section id="memberships" className={sectionCls("memberships")}>
            <div className="mb-4 rounded-md border border-teal-800 bg-teal-900/60 p-4 text-sm text-teal-50">
              <p className="font-semibold">Memberships, Trials, Top-Ups &amp; Credits (Summary)</p>
              <ul className={panelListCls}>
                <li>Memberships include feature access and a credit allotment per cycle; auto-renew until canceled.</li>
                <li>Credits don’t expire while active. After cancellation, unused credits remain for 24 months.</li>
                <li>Free Trial: one-time; details shown in-product.</li>
                <li>Top-Ups are instant and non-refundable.</li>
                <li>QACs: +0.10 credit per deleted incorrect item (cap +1.00/image) within 7 days.</li>
              </ul>
            </div>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>2) Memberships, Trials, Top-Ups &amp; Credits</h2>

              <h3 className={h3Cls}>2.1 Memberships (Non-Refundable)</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Memberships include feature access and credits per billing cycle. Billed at the published tier price; auto-renews until you cancel.</li>
                <li>Cancel any time; access continues through your Paid-Through Date. No pro‑rata refunds once a cycle starts.</li>
                <li>
                  If you upgrade or downgrade your membership, the change and associated billing
                  behavior are shown in‑product. Any existing Upload Credits remain in your account
                  and are combined with your new plan’s credit allotment; previously granted
                  credits are not re‑priced or refunded when you change plans.
                </li>
                <li><strong>Auto‑Renew Disclosures:</strong> Where required (e.g., CA/NY/CO/DE/IL), we present clear terms before purchase, send post‑purchase acknowledgment with cancellation instructions, provide an easy in‑app Cancel button, and send renewal reminders where applicable.</li>
              </ul>

              <h3 className={h3Cls}>2.2 Upload Credits — Use &amp; 24‑Month Rule</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Use:</strong> 1 photo = 1 Upload Credit, charged at upload.</li>
                <li><strong>While Active:</strong> Credits do not expire while your plan is active.</li>
                <li><strong>After Cancellation/Lapse:</strong> Unused Upload Credits remain on your account for 24 months from your Paid‑Through Date (the “Grace Window”). Reactivate any time in that window to use them.</li>
                <li><strong>Expiration:</strong> Unused credits expire at the end of the Grace Window and aren’t reinstated or refunded.</li>
                <li><strong>Courtesy Restore:</strong> Within 30 days after expiration you may request a one‑time courtesy restore—granted at our discretion.</li>
                <li><strong>Not Cash/Stored Value:</strong> Credits are not money, not redeemable, and not transferable.</li>
                <li>
                  <strong>Single Balance (No Re-Pricing):</strong> All Upload Credits in your account
                  (including credits from trials, memberships, upgrades, promotions, QACs, and
                  Top-Ups) are pooled into a single balance and used interchangeably. We do not
                  assign a specific monetary value to individual credits or re-price or refund
                  prior purchases when you change plans.
                </li>
                <li><strong>Unclaimed‑Property Compliance:</strong> If any jurisdiction treats purchased credits as subject to unclaimed‑property rules, we’ll comply (which may include remitting value and closing affected balances).</li>
              </ul>

              <h3 className={h3Cls}>2.3 Free Trial</h3>
              <p>One‑time Free Trial for new users (current offer shown in‑product). When the trial ends, dashboard/exports/printing pause until you upgrade.</p>

              <h3 className={h3Cls}>2.4 Top‑Ups (Non‑Refundable)</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Buy extra credits while your plan is active. Pack sizes/prices shown in‑product; may change with notice under §11.</li>
                <li>Non‑refundable. If you cancel, remaining credits follow the 24‑month rule.</li>
              </ul>

              <h3 className={h3Cls}>2.5 Trial Top‑Ups (Optional)</h3>
              <p>During a Free Trial we may offer small Trial Top‑Ups (e.g., 10–25 credits) at a trial rate, with anti‑abuse checks. Instant and non‑refundable. If you upgrade before the trial ends, unused Trial Top‑Ups convert to regular Upload Credits.</p>

              <h3 className={h3Cls}>2.6 Quality Adjustment Credits (QACs)</h3>
              <p>Delete incorrect AI‑derived items within 7 days of image upload to receive +0.10 credit per deleted item, capped at +1.00 per image. We may request reason codes and review for abuse.</p>

              <h3 className={h3Cls}>2.7 Payments, Taxes &amp; Chargebacks</h3>
              <p>Prices exclude taxes; you’re responsible for applicable taxes. Payments are processed by Stripe; we do not store full card numbers. If a chargeback is opened for a valid purchase, we may pause access until resolved.</p>

              <h3 className={h3Cls}>2.8 Tier Features &amp; Limits</h3>
              <p>Feature limits (e.g., batch size, concurrent sales) are shown in‑product and may change with notice under §11. Attempts to circumvent caps or rate limits are prohibited (see §4).</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">
                Back to top ↑
              </a>
            </div>
          </section>

          {/* 3) After Your Plan Ends */}
          <section id="after" className={sectionCls("after")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>3) If You Take a Break</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>When your plan ends, access to Inventory Dashboard, CSV/PDF exports, and price‑tag printing is paused.</li>
                <li>We retain your previously created data for 90 days so you can reactivate and keep working without losing your place.</li>
                <li>At our discretion (or if required by law), we may provide a one‑time export upon verified request during the 90‑day window.</li>
                <li>After 90 days, we may tidy up storage under our retention policy.</li>
                <li><em>Note:</em> The 24‑month credit window is separate from data retention. You can still reactivate later within 24 months to use remaining credits; previously created data may be unavailable after 90 days.</li>
              </ul>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 4) Acceptable Use */}
          <section id="acceptable" className={sectionCls("acceptable")}>
            <div className="mb-4 rounded-md border border-slate-700 bg-slate-800/70 p-4 text-sm text-slate-100">
              <p className="font-semibold">Acceptable Use (Keep It Professional)</p>
              <ul className={panelListCls}>
                <li>No illegal, harmful, or rights‑infringing content; no scraping or bypassing limits.</li>
                <li>Flagged content may be reviewed; violations can mean warnings, suspension, or bans.</li>
                <li>We maintain a repeat‑infringer policy under the DMCA.</li>
              </ul>
            </div>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>4) Acceptable Use</h2>
              <p>You agree not to upload or do anything illegal, harmful, or abusive. Specifically, you won’t upload content that is illegal; infringes IP or privacy/publicity rights; contains payment‑card data, government IDs, Social Security numbers, or sensitive health/biometric data; exploits minors; or contains malware. You won’t scrape or bulk‑export beyond provided tools; reverse‑engineer; access non‑public areas; bypass rate limits; misrepresent identity; or resell our Service without permission. We may rate‑limit, suspend, or terminate accounts for violations or suspected fraud. Where lawful and appropriate, we’ll notify you with available recourse.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 5) Privacy */}
          <section id="privacy" className={sectionCls("privacy")}>
            <div className="mb-4 rounded-md border border-emerald-800 bg-emerald-900/60 p-4 text-sm text-emerald-50">
              <p className="font-semibold">Privacy (Clear & Practical)</p>
              <ul className={panelListCls}>
                <li>You own your original images and have broad rights to AI‑derived inventory data.</li>
                <li>We use data to run and improve Scucci (including de‑identified/anonymized model improvement). DPA available to disable training for enterprise.</li>
                <li>We do not sell your personal data.</li>
              </ul>
            </div>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>5) Privacy (Clear & Practical)</h2>
              <p><strong>Roles.</strong> For Account Data, Scucci is a “controller.” For Content you upload (e.g., estate images), Scucci acts as a “processor” to you (or your business) as “controller,” subject to an optional Data Processing Addendum (DPA) available on request.</p>
              <p><strong>What we collect.</strong> Account: name, email, membership/billing metadata. Content: photos you upload and items/tags generated. Usage: feature use, timestamps, device/browser, IP, uploads/deletions, credit/QAC actions. Payments: via Stripe (we don’t store full card numbers).</p>
              <p><strong>What we use it for.</strong> Operate the Service, prevent abuse, improve quality and reliability, provide support, enforce terms, and meet legal obligations.</p>
              <p><strong>Model improvement.</strong> We may use de‑identified/anonymized data to improve quality and safety. If you need a DPA that disables training on your content, request one—we’ll honor it once signed.</p>
              <p><strong>Sharing.</strong> We don’t sell your personal data. We share only with service providers (hosting, AI infra, payments, analytics) bound by confidentiality/purpose limits; legal/compliance; business transfers; or with your consent.</p>
              <p><strong>Security.</strong> Encryption in transit and at rest, role‑based access, least‑privilege, logging, and tested backups. No system is 100% secure.</p>
              <p><strong>Security incidents.</strong> If we discover a security incident affecting personal data, we’ll investigate, mitigate, and notify affected users and/or authorities without undue delay where required by law.</p>
              <p><strong>Your rights.</strong> Where applicable (e.g., GDPR/CCPA/CPRA), you may request access, correction, deletion, restriction, or portability—email support@scuccisystems.com. Authorized‑agent requests need proof of authorization. If we deny a request, reply “APPEAL”; we’ll review and respond within required timelines.</p>
              <p><strong>International transfers.</strong> Where data is transferred internationally, we rely on appropriate safeguards (e.g., SCCs/UK Addendum or equivalent).</p>
              <p><strong>Cookies & tracking.</strong> We use cookies for login, security, analytics, and product improvement.</p>
              <p><strong>Marketing & testimonials.</strong> We won’t publicly use your name, business, images, or outputs without your explicit written consent. You can withdraw consent; we’ll stop future use with reasonable processing time.</p>
              <p><strong>Children.</strong> Not for users under 13 (or under 16 where applicable). The Service is for adults 18+.</p>
              <p><strong>California privacy contact.</strong> support@scuccisystems.com</p>
              <p><strong>Retention.</strong> We keep data while your account is active and for a reasonable time after to meet legal/operational needs. Deleted items may remain in backups briefly.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 6) Refunds & Billing Issues */}
          <section id="refunds" className={sectionCls("refunds")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>6) Refunds & Billing Issues</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Memberships, Upload Credits (including credits granted in memberships, promotions,
                  QACs, and Trial or standard Top-Ups), and other usage-based fees are
                  non-refundable, except where a refund is required by law.
                </li>
                <li>If there’s a billing error, duplicate charge, or true hardship, email support@scuccisystems.com. We will review with care and try to make it right.</li>
                <li>
                  We do not convert unused Upload Credits into cash or other forms of refund. In
                  rare cases of confirmed billing error or technical failure on our side, we may
                  restore credits or issue a partial refund at our discretion.
                </li>
                <li>Chargebacks for valid charges may result in suspension until resolved.</li>
              </ul>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 7) Disclaimers & Limits */}
          <section id="disclaimers" className={sectionCls("disclaimers")}>
            <div className="mb-4 rounded-md border border-sky-800 bg-sky-900/60 p-4 text-sm text-sky-100">
              <p className="font-semibold">Important Disclaimers & Limits (Summary)</p>
              <ul className={panelListCls}>
                <li>
                  Service is provided <strong>AS IS</strong>; AI can be wrong; back up your data.
                </li>
                <li>Our liability is capped to what you paid in the last 12 months.</li>
                <li>We may change or discontinue parts of the Service.</li>
              </ul>
            </div>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>7) Important Disclaimers & Limits</h2>
              <p><strong>Service Interruptions & Maintenance.</strong> Planned or unplanned outages may occur. Interruptions do not entitle you to refunds, extensions, or additional credits.</p>
              <p><strong>No warranties.</strong> The Service is provided “AS IS” and “AS AVAILABLE.” No express or implied warranties (merchantability, fitness, non‑infringement).</p>
              <p><strong>AI accuracy.</strong> AI can be wrong. Always review before printing/publishing. The sole remedy for item‑level errors is QACs (§2.6).</p>
              <p><strong>No professional advice.</strong> Outputs are not legal, medical, financial, appraisal, or other professional advice.</p>
              <p><strong>Liability cap.</strong> To the fullest extent allowed by law, Scucci’s total liability for all claims is limited to the fees you paid to Scucci in the 12 months before the event. No indirect, special, incidental, consequential, or punitive damages.</p>
              <p><strong>Third‑party platforms.</strong> You’re responsible for marketplace compliance (e.g., prohibited items, authenticity, condition labels).</p>
              <p><strong>Data availability & backups.</strong> We strive for reliability and maintain backups, but do not guarantee permanent storage or uninterrupted access. Export important data regularly.</p>
              <p><strong>Business continuity & portability.</strong> If we discontinue the Service, we’ll make a good‑faith effort to enable exports before shutdown, but cannot guarantee continued access thereafter.</p>
              <p><strong>API / automation.</strong> No public API at launch. If we later offer one, separate terms will apply.</p>
              <p><strong>Service changes.</strong> We may add, change, or discontinue features at any time.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 8) Beta Features */}
          <section id="beta" className={sectionCls("beta")}>
            <div className="mb-4 rounded-md border border-sky-800 bg-sky-900/60 p-4 text-sm text-sky-100">
              <p className="font-semibold">Beta Features</p>
              <ul className={panelListCls}>
                <li>Pre‑release, may break, and can change or be removed anytime.</li>
                <li>Data in Beta can be lost or reset — do not rely on Beta for production data.</li>
                <li>Feedback you share belongs to Scucci; please keep Beta details confidential.</li>
              </ul>
            </div>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>8) Beta Features</h2>
              <p>If you opt into Beta features: they may change or break, can be removed at any time, and data may be reset. Use Beta for evaluation only.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 9) Dispute Resolution */}
          <section id="dispute" className={sectionCls("dispute")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>9) Dispute Resolution (Arbitration) — Easy Opt-Out</h2>
              <p>We aim to resolve issues quickly by email. If we can’t, disputes will be resolved by binding arbitration under the American Arbitration Association (AAA) rules in Atlanta, Georgia (remote/document‑only options may apply).</p>
              <p><strong>No class actions.</strong> Each of us brings claims individually.</p>
              <p><strong>Small‑claims & IP.</strong> Either party may file in small‑claims court or seek temporary injunctive relief to protect IP/confidential information.</p>
              <p><strong>Fees.</strong> The AAA rules govern fee allocation; where required by the Consumer Rules, we pay AAA fees beyond any capped consumer filing fee.</p>
              <p><strong>Your opt‑out right.</strong> You can opt out of arbitration within 30 days of first acceptance by emailing support@scuccisystems.com with your name, mailing address, account email, and a statement opting out. If you opt out, disputes go to the state or federal courts in Fulton County, Georgia.</p>
              <p><strong>Governing law.</strong> Georgia law applies (conflict‑of‑laws rules excluded).</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 10) Age, Eligibility & Compliance */}
          <section id="eligibility" className={sectionCls("eligibility")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>10) Age, Eligibility & Compliance</h2>
              <p>The Service is for adults 18+. You agree to follow applicable laws and these terms. We may suspend access where continued service would violate law.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 11) Changes to Prices or Terms */}
          <section id="changes" className={sectionCls("changes")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>11) Changes to Prices or Terms</h2>
              <p>We may change prices, features, or terms. Material changes (including price changes) will be noticed in‑app and/or by email at least 30 days in advance. Continued use after the effective date means you accept the updated terms.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 12) Intellectual Property & DMCA */}
          <section id="ip" className={sectionCls("ip")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>12) Intellectual Property & DMCA</h2>
              <p>Scucci owns the platform and its IP (software, UI/UX, models, brand). Your original content remains yours; you grant us a license to process it to operate, secure, and improve the Service.</p>
              <p>
                <strong>DMCA Agent — Scucci Systems LLC</strong><br />
                2870 Peachtree Rd NW #915-4807, Atlanta, GA 30305<br />
                support@scuccisystems.com
              </p>
              <p>We respond to DMCA notices under 17 U.S.C. §512 and maintain a repeat‑infringer policy.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 13) Indemnification */}
          <section id="indemnification" className={sectionCls("indemnification")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>13) Indemnification</h2>
              <p>You agree to indemnify, defend, and hold harmless Scucci Systems LLC and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys’ fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; or (c) your Content (including intellectual‑property, privacy, or consumer‑protection claims).</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 14) Accessibility & Contact */}
          <section id="accessibility" className={sectionCls("accessibility")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>14) Accessibility & Contact</h2>
              <p>We strive to make Scucci accessible. If you hit a barrier or need help, let us know.</p>
              <p><strong>Support & General:</strong> support@scuccisystems.com</p>
              <p><strong>Mail:</strong> Scucci Systems LLC, 2870 Peachtree Rd NW #915-4807, Atlanta, GA 30305</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>

          {/* 15) Legal Boilerplate */}
          <section id="boilerplate" className={sectionCls("boilerplate")}>
            <div className={bodyTextCls}>
              <h2 className={h2Cls}>15) Legal Boilerplate</h2>
              <p><strong>How you agree.</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>At registration:</strong> A required checkbox states “I’m 18+ and I agree to the Scucci Terms & Privacy (binding arbitration, class‑action waiver).” Registration is blocked if unchecked.</li>
                <li><strong>At purchase/upgrade:</strong> “By purchasing, you agree to the Terms & Privacy. Memberships/Top‑Ups are non‑refundable. Credits remain for 24 months after cancellation.”</li>
              </ul>
              <p><strong>Policy hierarchy.</strong> If these Terms conflict with a supplemental policy, these Terms control unless the supplemental policy expressly states otherwise. Negotiated enterprise agreements may supersede for those parties.</p>
              <p><strong>Survival.</strong> Fee/credits, privacy, IP, disclaimers, liability limits, arbitration, and indemnity survive termination.</p>
              <p><strong>Reservation of rights.</strong> All rights not expressly granted are reserved by Scucci.</p>
              <p><strong>Severability.</strong> If one part is invalid, the rest still applies.</p>
              <p><strong>Assignment.</strong> You can’t transfer your account without consent; we may assign as part of a merger/acquisition or similar change.</p>
              <p><strong>Force majeure.</strong> Not liable for events beyond reasonable control (outages, disasters, strikes).</p>
              <p><strong>E‑communications.</strong> You consent to receive notices electronically (email/in‑product).</p>
              <p><strong>Legal holds.</strong> We may preserve data and withhold deletion if subject to legal holds, investigations, or court orders; where permitted, we’ll notify you.</p>
              <p><strong>Export controls & sanctions.</strong> You are not in a sanctioned country or on a restricted list and will comply with export/sanctions laws (e.g., EAR/OFAC).</p>
              <p><strong>Entire agreement.</strong> These Terms are the entire agreement between you and Scucci and supersede prior understandings.</p>
              <p className="mt-6 text-sm text-slate-300 print:text-black/70">Patent‑pending (no guarantee of issuance) • © 2025 Scucci Systems LLC. All rights reserved. Scucci™ and unregistered marks are claimed under common law; third‑party marks belong to their owners.</p>
            </div>
            <div className="mt-4">
              <a href="#top" onClick={(e) => handleTocClick(e, "top")} className="text-slate-300 hover:text-white underline underline-offset-4">Back to top ↑</a>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
